/**
 * Defer Image Zoom and Modal Loading for Better LCP
 *
 * This script defers the initialization of image zoom and modal components
 * until AFTER the LCP (Largest Contentful Paint) has completed.
 *
 * Benefits:
 * - Reduces initial HTML parsing time
 * - Reduces JavaScript execution during critical render path
 * - Allows LCP image to load faster
 * - Image zoom and modal only load when user interacts
 */

(function() {
  'use strict';

  // Only run on product pages
  if (window.template !== 'product' && !document.body.classList.contains('template-product')) {
    return;
  }

  let imageZoomInitialized = false;
  let modalRemoved = false;

  /**
   * Remove or defer modal HTML that's blocking LCP
   * Modals are typically large HTML blocks that don't need to be in initial DOM
   */
  function deferModals() {
    if (modalRemoved) return;

    // Find all modal components except critical ones
    const modals = document.querySelectorAll('modal[data-vue]:not(.modal--critical)');

    if (modals.length > 0) {
      console.log(`🚫 Deferring ${modals.length} non-critical modals to improve LCP`);

      // Store modal content in memory for later restoration
      modals.forEach((modal, index) => {
        const modalId = modal.getAttribute('id') || `modal-${index}`;
        const modalContent = modal.innerHTML;

        // Create a lightweight placeholder
        const placeholder = document.createComment(`Deferred modal: ${modalId}`);
        modal.parentNode.replaceChild(placeholder, modal);

        // Store for restoration on first interaction
        modal.setAttribute('data-deferred', 'true');
        modal._deferredContent = modalContent;
        modal._placeholder = placeholder;

        console.log(`✓ Deferred modal: ${modalId}`);
      });

      modalRemoved = true;
    }
  }

  /**
   * Initialize image zoom only after LCP
   */
  function initImageZoomAfterLCP() {
    if (imageZoomInitialized) return;

    // Find the product-images component
    const productImagesComponent = document.querySelector('product-images');

    if (productImagesComponent) {
      console.log('🔍 Found product-images component, will initialize zoom after LCP');

      // Wait for LCP to complete (approximately 2-3 seconds)
      setTimeout(function() {
        if (imageZoomInitialized) return;

        // Enable image zoom by removing disabled state if any
        const zoomDisabled = productImagesComponent.getAttribute('image-zoom-disabled');

        if (zoomDisabled === 'true') {
          productImagesComponent.removeAttribute('image-zoom-disabled');
          console.log('✅ Image zoom enabled after LCP');
        }

        imageZoomInitialized = true;

        if (typeof performance !== 'undefined' && performance.mark) {
          performance.mark('image-zoom-initialized');
        }
      }, 2500); // 2.5 seconds after page load - after LCP typically completes
    }
  }

  /**
   * Restore deferred modals on user interaction
   */
  function restoreModalsOnInteraction() {
    // Restore on any click
    document.addEventListener('click', function restoreModals(e) {
      const deferredModals = document.querySelectorAll('[data-deferred="true"]');

      if (deferredModals.length > 0) {
        console.log('🔄 Restoring deferred modals on user interaction');

        deferredModals.forEach(modal => {
          const placeholder = modal._placeholder;
          const content = modal._deferredContent;

          if (placeholder && content) {
            modal.innerHTML = content;
            modal.removeAttribute('data-deferred');
            placeholder.parentNode.replaceChild(modal, placeholder);
            console.log(`✓ Restored modal: ${modal.getAttribute('id')}`);
          }
        });

        // Remove event listener after restoration
        document.removeEventListener('click', restoreModals);
      }
    }, { once: true });
  }

  /**
   * Initialize optimizations
   */
  function init() {
    // Use requestIdleCallback if available for non-critical work
    if ('requestIdleCallback' in window) {
      // Defer modals immediately during idle time
      window.requestIdleCallback(function() {
        deferModals();
      }, { timeout: 2000 });

      // Initialize image zoom after LCP
      window.requestIdleCallback(function() {
        initImageZoomAfterLCP();
      }, { timeout: 3000 });

      // Set up interaction listener
      window.requestIdleCallback(function() {
        restoreModalsOnInteraction();
      }, { timeout: 1000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
          setTimeout(deferModals, 100);
          setTimeout(initImageZoomAfterLCP, 2500);
          setTimeout(restoreModalsOnInteraction, 100);
        });
      } else {
        setTimeout(deferModals, 100);
        setTimeout(initImageZoomAfterLCP, 2500);
        setTimeout(restoreModalsOnInteraction, 100);
      }
    }
  }

  // Start optimizations
  console.log('🚀 Starting image zoom and modal defer optimizations...');
  init();
})();
